import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import './HowItWorks.css';

const STEPS = [
    {
        n: '01',
        title: 'You say what actually happened',
        body: "Onboarding is three questions — your name, your situation, and what's genuinely frustrating you. After that you just talk. Screenshots work too: paste or drop up to five images into any message, and the coach keeps them in view for the rest of the session.",
    },
    {
        n: '02',
        title: 'It answers from a real body of knowledge',
        body: 'Replies are grounded in a curated corpus rather than improvised. When the coach draws on that material the response carries its sources, so you can see where an answer came from.',
    },
    {
        n: '03',
        title: 'It remembers, and memory compounds',
        body: "Every session updates a running model of you — not a transcript dump, but a distilled picture of your patterns and what you've already tried. Advice in month three is built on months one and two.",
    },
    {
        n: '04',
        title: 'It gives you something to do',
        body: 'When a conversation reaches a real conclusion, the coach proposes a concrete action with a time estimate. You accept it and it lands in your Actions list. Nothing is assigned without your say-so.',
    },
    {
        n: '05',
        title: 'You report back from the field',
        body: 'Log what happened when you tried it. Field reports are the feedback signal — what lets the coach tell the difference between advice that worked and advice that merely sounded good.',
    },
    {
        n: '06',
        title: 'Your profile shows the trend line',
        body: 'The Profile view synthesizes everything into what is working, where you keep getting stuck, how far along each skill is, and what to focus on next. Generated from your history, not from a quiz.',
    },
];

const LANES = [
    { x: 90, title: 'Browser', sub: 'React SPA' },
    { x: 270, title: 'Cognito', sub: 'Identity' },
    { x: 480, title: 'Lambda', sub: 'Python router', accent: true },
    { x: 700, title: 'Supermemory', sub: 'Corpus search' },
    { x: 900, title: 'Bedrock', sub: 'Claude' },
    { x: 1100, title: 'DynamoDB', sub: 'Eight tables' },
];

// [fromX, toX, y, label, isReturn]
const MESSAGES = [
    [90, 270, 104, 'getSession()', false],
    [270, 90, 142, 'ID token (JWT)', true],
    [90, 474, 186, 'POST /api/chat  ·  Bearer JWT  ·  message + images', false],
    [486, 1100, 276, 'load onboarding · user facts · session history', false],
    [1100, 486, 314, 'memory prefix', true],
    [486, 900, 356, 'plan this turn', false],
    [900, 486, 394, 'JSON plan of steps', true],
    [486, 700, 436, 'search_corpus(query)', false],
    [700, 486, 474, 'matching chunks + source titles', true],
    [486, 900, 516, 'reply_to_user  ·  assembled context + images', false],
    [900, 486, 554, 'reply', true],
    [486, 900, 596, 'action-card decision  ·  suggestion chips', false],
    [486, 1100, 638, 'persist turn · field report · user context', false],
    [474, 90, 680, 'reply  ·  proposed_action  ·  sources  ·  suggestions', true],
];

const STACK = [
    {
        label: 'Frontend',
        items: [
            ['React 18 SPA', 'Function components and hooks throughout, routed with React Router v6. Two context providers — auth and chat — hold all cross-page state. No external state library.'],
            ['Client-side image pipeline', 'Attachments are validated, downscaled on a canvas to a 1568px longest edge and re-encoded to JPEG in the browser before upload. GIFs pass through untouched under 3 MB. Keeps payloads small and stays inside the vision model input budget.'],
            ['Session continuity', 'The in-flight conversation is rehydrated from the server on reload, so a refresh never loses the thread.'],
        ],
    },
    {
        label: 'Authentication',
        items: [
            ['Amazon Cognito', 'A dedicated user pool driven directly from the browser over SRP — no Amplify client layer. Sign-up with emailed confirmation codes, sign-in, and full forgot/reset password flows.'],
            ['JWT-bearer API', 'The ID token is attached to every authenticated request. The backend derives identity from the verified token, never from client-supplied fields.'],
        ],
    },
    {
        label: 'Backend',
        items: [
            ['Single Python 3.10 Lambda', 'One 1024 MB function with a 120-second timeout serves the entire API behind a Lambda Function URL. Consolidating routes into one function keeps warm-start behavior predictable and deploys atomic.'],
            ['REST surface', 'Chat, onboarding, profile, profile snapshot, suggestions, actions CRUD, abuse reports, anonymous session ping, and full account reset.'],
            ['Serverless throughout', 'No servers, containers, or VPC to operate. Cost tracks usage and idle cost is effectively zero.'],
        ],
    },
    {
        label: 'Intelligence',
        items: [
            ['Claude on Amazon Bedrock', 'Inference runs through Bedrock inference profiles — regional and global — so capacity can shift across regions without a code change. IAM scopes model access to Anthropic models only.'],
            ['Multimodal', 'Screenshots go to the model alongside text, which is what makes "here is the actual conversation, read it" work.'],
            ['Structured output', 'A chat turn returns the reply plus optional typed fields: proposed action and time estimate, source citations, a profile-updated signal, a field-report-logged signal, and follow-up suggestions. The UI renders each as a distinct affordance instead of parsing them out of prose.'],
        ],
    },
    {
        label: 'Agent loop',
        items: [
            ['Plan, then execute', 'A turn does not go straight to the model. A first call returns a JSON plan naming the steps to run — searching the corpus, logging a field report, saving a new fact about you — and the plan always ends in the reply. If the planner omits that final step, the server appends it.'],
            ['Steps are tools', 'Logging a field report and updating your saved context are real tool calls with typed schemas, so the model fills in structured fields rather than emitting prose the server has to parse back out.'],
            ['Post-reply passes', 'Two further calls run after the reply exists: one decides whether it earned an action card and estimates how long the action takes, another drafts the follow-up chips. Judging the reply after writing it produces better action cards than deciding up front.'],
            ['Degrades rather than fails', 'A malformed plan falls back to a plain reply, and a failed suggestion pass surfaces as a dismissible toast instead of breaking the turn.'],
        ],
    },
    {
        label: 'Memory & retrieval',
        items: [
            ['Corpus retrieval via Supermemory', 'The coaching knowledge base is searched through Supermemory, which returns relevance-filtered chunks along with their source titles. Those titles become the citations attached to a reply. Retrieval itself costs zero model tokens.'],
            ['Memory prefix', 'Onboarding answers and the accumulated set of user facts are assembled into a prefix prepended to every prompt in a turn, so each model call in the chain reasons about the same person.'],
            ['Corpus data in S3', 'The index and transcripts that back the knowledge base are published to an S3 bucket by the deploy pipeline, keeping the corpus versioned alongside the code that uses it.'],
            ['Screenshots persist for the session', 'An attached image is transcribed once by a dedicated vision pass, then that text is carried forward so later turns still have the screenshot available without re-sending it.'],
        ],
    },
    {
        label: 'Data model',
        items: [
            ['DynamoDB, eight tables', 'Conversations, field reports, actions and the memory document are partitioned by user, giving single-digit-millisecond reads on the hot path and tenant isolation by key design.'],
            ['On-demand billing', 'Every table is pay-per-request, so the data layer costs nothing at rest and needs no capacity planning.'],
            ['Operational tables', 'Separate tables cover per-day usage metering, rate limiting, anonymous visitor attribution, and moderation reports on a seven-day retention window.'],
        ],
    },
    {
        label: 'Operations & safety',
        items: [
            ['Least-privilege IAM', 'The function role is composed of narrowly scoped inline policies — one per table, plus read-only object storage, model invocation and metrics. No wildcard resource grants.'],
            ['Rate limiting', 'Per-user throttles are enforced server-side and surface to the client as a clean 429 rather than a failure.'],
            ['Abuse reporting', 'Any conversation can be submitted for review from the chat header. Reports auto-expire after seven days.'],
            ['Observability', 'Structured logs plus custom CloudWatch metrics emitted from the function.'],
            ['Data deletion', 'A single action wipes all coaching memory, conversation history and actions while leaving the login intact.'],
        ],
    },
];

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif";

function Box({ x, y, w, h, accent = false }) {
    return (
        <rect
            x={x} y={y} width={w} height={h} rx="10"
            fill="#161616"
            stroke={accent ? '#4a3d28' : '#262626'}
            strokeWidth="1"
        />
    );
}

function Pill({ x, y, w, label }) {
    return (
        <g>
            <rect x={x} y={y} width={w} height="22" rx="11" fill="#1e1e1e" stroke="#2c2c2c" />
            <text x={x + w / 2} y={y + 15} textAnchor="middle" fill="#9a9a9a" fontSize="11" fontFamily={FONT}>
                {label}
            </text>
        </g>
    );
}

function ArchitectureDiagram() {
    const pills = [
        ['Splash', 62], ['Onboard', 72], ['Chat', 50],
        ['Profile', 64], ['Actions', 66], ['Settings', 70],
    ];
    let px = 140;
    const pillEls = pills.map(([label, w]) => {
        const el = <Pill key={label} x={px} y={92} w={w} label={label} />;
        px += w + 8;
        return el;
    });

    const leaves = [
        { x: 120, title: 'Amazon Bedrock', l1: 'Claude', l2: 'inference profiles' },
        { x: 320, title: 'Supermemory', l1: 'Corpus retrieval', l2: 'chunks + citations' },
        { x: 520, title: 'Amazon S3', l1: 'Corpus data', l2: 'published by CI' },
        { x: 720, title: 'DynamoDB', l1: 'Eight tables', l2: 'partitioned by user' },
    ];

    return (
        <div className="hiw-diagram-scroll">
            <svg
                className="hiw-diagram"
                viewBox="0 0 1200 572"
                role="img"
                aria-label="InTension system architecture: a React single-page app authenticated by Amazon Cognito, served through Amplify Hosting and CloudFront, calling a single Python Lambda behind a Function URL, which fans out to Amazon Bedrock, Supermemory, an S3 retrieval corpus and eight DynamoDB tables, with CloudWatch for observability."
            >
                <defs>
                    <marker id="hiw-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M0,0 L10,5 L0,10 z" fill="#4a4a4a" />
                    </marker>
                </defs>

                {/* CLIENT */}
                <Box x={120} y={20} w={780} h={104} />
                <text x={140} y={42} fill="#c8a46e" fontSize="10" letterSpacing="1.4" fontFamily={FONT} fontWeight="600">CLIENT</text>
                <text x={140} y={66} fill="#e8e8e8" fontSize="15" fontWeight="600" fontFamily={FONT}>React 18 SPA</text>
                <text x={140} y={84} fill="#888" fontSize="11.5" fontFamily={FONT}>React Router v6 · auth + chat context · canvas image pipeline</text>
                {pillEls}

                {/* COGNITO */}
                <Box x={930} y={20} w={250} h={104} />
                <text x={950} y={42} fill="#c8a46e" fontSize="10" letterSpacing="1.4" fontFamily={FONT} fontWeight="600">IDENTITY</text>
                <text x={950} y={66} fill="#e8e8e8" fontSize="15" fontWeight="600" fontFamily={FONT}>Amazon Cognito</text>
                <text x={950} y={84} fill="#888" fontSize="11.5" fontFamily={FONT}>User pool · SRP in browser</text>
                <text x={950} y={101} fill="#888" fontSize="11.5" fontFamily={FONT}>Issues ID token (JWT)</text>
                <path d="M900,72 H930" stroke="#4a4a4a" strokeWidth="1.2" strokeDasharray="4 3" markerEnd="url(#hiw-arrow)" fill="none" />

                {/* client -> edge */}
                <path d="M510,124 V166" stroke="#4a4a4a" strokeWidth="1.2" markerEnd="url(#hiw-arrow)" fill="none" />
                <text x={524} y={150} fill="#666" fontSize="11" fontFamily={FONT}>HTTPS · Authorization: Bearer &lt;JWT&gt;</text>

                {/* EDGE */}
                <Box x={120} y={166} w={780} h={72} />
                <text x={140} y={188} fill="#c8a46e" fontSize="10" letterSpacing="1.4" fontFamily={FONT} fontWeight="600">EDGE</text>
                <text x={140} y={212} fill="#e8e8e8" fontSize="15" fontWeight="600" fontFamily={FONT}>AWS Amplify Hosting + CloudFront</text>
                <text x={140} y={230} fill="#888" fontSize="11.5" fontFamily={FONT}>Static asset delivery · SPA rewrites to index.html · managed TLS</text>

                {/* edge -> compute */}
                <path d="M510,238 V280" stroke="#4a4a4a" strokeWidth="1.2" markerEnd="url(#hiw-arrow)" fill="none" />

                {/* COMPUTE */}
                <Box x={120} y={280} w={780} h={100} accent />
                <text x={140} y={302} fill="#c8a46e" fontSize="10" letterSpacing="1.4" fontFamily={FONT} fontWeight="600">COMPUTE</text>
                <text x={140} y={326} fill="#e8e8e8" fontSize="15" fontWeight="600" fontFamily={FONT}>Lambda Function URL — Python 3.10</text>
                <text x={140} y={344} fill="#888" fontSize="11.5" fontFamily={FONT}>Single function routes the whole API · 1024 MB · 120 s timeout</text>
                <text x={140} y={364} fill="#666" fontSize="11" fontFamily={FONT}>
                    /api/chat · /api/onboard · /api/profile · /api/suggestions · /api/actions · /api/report · /api/ping
                </text>

                {/* CLOUDWATCH */}
                <Box x={930} y={280} w={250} h={100} />
                <text x={950} y={302} fill="#c8a46e" fontSize="10" letterSpacing="1.4" fontFamily={FONT} fontWeight="600">OBSERVABILITY</text>
                <text x={950} y={326} fill="#e8e8e8" fontSize="15" fontWeight="600" fontFamily={FONT}>CloudWatch</text>
                <text x={950} y={344} fill="#888" fontSize="11.5" fontFamily={FONT}>Structured logs</text>
                <text x={950} y={361} fill="#888" fontSize="11.5" fontFamily={FONT}>Custom metrics</text>
                <path d="M900,330 H930" stroke="#4a4a4a" strokeWidth="1.2" strokeDasharray="4 3" markerEnd="url(#hiw-arrow)" fill="none" />

                {/* fan-out bus */}
                <path d="M510,380 V400" stroke="#4a4a4a" strokeWidth="1.2" fill="none" />
                <path d="M210,400 H810" stroke="#4a4a4a" strokeWidth="1.2" fill="none" />
                {leaves.map(l => (
                    <path key={l.x} d={`M${l.x + 90},400 V430`} stroke="#4a4a4a" strokeWidth="1.2"
                        markerEnd="url(#hiw-arrow)" fill="none" />
                ))}

                {/* leaves */}
                {leaves.map(l => (
                    <g key={l.title}>
                        <Box x={l.x} y={430} w={180} h={92} />
                        <text x={l.x + 20} y={462} fill="#e8e8e8" fontSize="14" fontWeight="600" fontFamily={FONT}>{l.title}</text>
                        <text x={l.x + 20} y={482} fill="#888" fontSize="11.5" fontFamily={FONT}>{l.l1}</text>
                        <text x={l.x + 20} y={500} fill="#666" fontSize="11" fontFamily={FONT}>{l.l2}</text>
                    </g>
                ))}

                <text x={900} y={550} textAnchor="end" fill="#555" fontSize="10.5" fontFamily={FONT}>
                    conversations · field reports · actions · memory · usage · rate limits · visitors · reports
                </text>
            </svg>
        </div>
    );
}

function SequenceDiagram() {
    return (
        <div className="hiw-diagram-scroll">
            <svg
                className="hiw-diagram"
                viewBox="0 0 1200 780"
                role="img"
                aria-label="Sequence diagram for a single chat message. The browser gets an ID token from Cognito, posts the message and images to the Lambda with a bearer token. The Lambda verifies the token, derives identity and checks the rate limit, loads the profile document and usage counters from DynamoDB, pulls semantic recall and corpus sources from retrieval, sends assembled context and images to Claude on Bedrock, persists the turn and updated memory back to DynamoDB, then returns the reply with its proposed action, sources and suggestions."
            >
                <defs>
                    <marker id="hiw-seq-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                        markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                        <path d="M0,0 L10,5 L0,10 z" fill="#5a5a5a" />
                    </marker>
                </defs>

                {/* lifelines */}
                {LANES.map(l => (
                    <line key={`ll-${l.x}`} x1={l.x} y1="66" x2={l.x} y2="716"
                        stroke="#2a2a2a" strokeWidth="1" strokeDasharray="3 5" />
                ))}

                {/* participant headers */}
                {LANES.map(l => (
                    <g key={l.title}>
                        <rect x={l.x - 70} y="8" width="140" height="58" rx="9"
                            fill="#161616" stroke={l.accent ? '#4a3d28' : '#262626'} />
                        <text x={l.x} y="34" textAnchor="middle" fill="#e8e8e8"
                            fontSize="14" fontWeight="600" fontFamily={FONT}>{l.title}</text>
                        <text x={l.x} y="52" textAnchor="middle" fill="#888"
                            fontSize="11" fontFamily={FONT}>{l.sub}</text>
                    </g>
                ))}

                {/* activation bar on the Lambda lifeline */}
                <rect x="474" y="186" width="12" height="502" rx="2"
                    fill="#1f1a12" stroke="#4a3d28" />

                {/* self-call */}
                <path d="M486,214 H544 V240 H486" fill="none" stroke="#5a5a5a"
                    strokeWidth="1.2" markerEnd="url(#hiw-seq-arrow)" />
                <text x="554" y="232" fill="#888" fontSize="11.5" fontFamily={FONT}>
                    verify JWT · derive identity · check rate limit
                </text>

                {/* messages */}
                {MESSAGES.map(([x1, x2, y, label, isReturn]) => (
                    <g key={`${y}-${label}`}>
                        <line x1={x1} y1={y} x2={x2} y2={y}
                            stroke="#5a5a5a" strokeWidth="1.2"
                            strokeDasharray={isReturn ? '5 4' : undefined}
                            markerEnd="url(#hiw-seq-arrow)" />
                        <text x={(x1 + x2) / 2} y={y - 9} textAnchor="middle"
                            fill={isReturn ? '#777' : '#9a9a9a'} fontSize="11.5" fontFamily={FONT}>
                            {label}
                        </text>
                    </g>
                ))}

                {/* legend */}
                <g>
                    <line x1="20" y1="754" x2="52" y2="754" stroke="#5a5a5a" strokeWidth="1.2" />
                    <text x="60" y="758" fill="#666" fontSize="11" fontFamily={FONT}>request</text>
                    <line x1="126" y1="754" x2="158" y2="754" stroke="#5a5a5a" strokeWidth="1.2" strokeDasharray="5 4" />
                    <text x="166" y="758" fill="#666" fontSize="11" fontFamily={FONT}>response</text>
                </g>
            </svg>
        </div>
    );
}

export default function HowItWorksPage() {
    const navigate = useNavigate();
    const auth = useAuth();
    const isAuthed = !!auth?.user;
    const techRef = useRef(null);

    // .hiw-page is the scroll container, not the window. Measure the target
    // against it with rects (offsetTop depends on offsetParent) and scroll only
    // that element, so the jump can't leak to an ancestor scroller.
    const scrollToTech = () => {
        const tech = techRef.current;
        const page = tech && tech.closest('.hiw-page');
        if (!tech || !page) return;
        const top = tech.getBoundingClientRect().top
            - page.getBoundingClientRect().top
            + page.scrollTop
            - 56;
        page.scrollTo({ top, behavior: 'smooth' });
    };

    return (
        <div className="hiw-page">
            <header className="hiw-topbar">
                <button className="hiw-brand" onClick={() => navigate('/')}>
                    <span className="hiw-brand-dot" />
                    InTension
                </button>
                <div className="hiw-topbar-right">
                    <span className="hiw-topbar-crumb">How it works</span>
                    {isAuthed ? (
                        <button className="hiw-btn small primary" onClick={() => navigate('/')}>
                            Open chat
                        </button>
                    ) : (
                        <>
                            <button className="hiw-topbar-link" onClick={() => navigate('/auth?mode=signin')}>
                                Sign in
                            </button>
                            <button className="hiw-btn small primary" onClick={() => navigate('/auth')}>
                                Get started
                            </button>
                        </>
                    )}
                </div>
            </header>

            <div className="hiw-content">
                <section className="hiw-hero">
                    <div className="hiw-hero-main">
                        <h1 className="hiw-hero-title">How InTension works</h1>
                        <p className="hiw-hero-sub">
                            The coaching loop on the surface, and the serverless system
                            underneath it — how a conversation becomes a durable model of one
                            person, and how that model shapes the next answer.
                        </p>
                        <div className="hiw-hero-actions">
                            <button className="hiw-skip" onClick={scrollToTech}>
                                Skip to technical details
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M12 5v14M5 12l7 7 7-7" />
                                </svg>
                            </button>
                        </div>
                    </div>
                    <dl className="hiw-hero-facts">
                        <div><dt>Frontend</dt><dd>React 18 SPA</dd></div>
                        <div><dt>Backend</dt><dd>Python on AWS Lambda</dd></div>
                        <div><dt>Model</dt><dd>Claude via Amazon Bedrock</dd></div>
                        <div><dt>Storage</dt><dd>DynamoDB · Supermemory</dd></div>
                    </dl>
                </section>

                <section className="hiw-section">
                    <h2 className="hiw-h2">The loop</h2>
                    <div className="hiw-steps">
                        {STEPS.map(s => (
                            <article className="hiw-step" key={s.n}>
                                <div className="hiw-step-num">{s.n}</div>
                                <h3>{s.title}</h3>
                                <p>{s.body}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="hiw-tech-divider" ref={techRef}>
                    <span>Technical details</span>
                </section>

                <section className="hiw-section">
                    <h2 className="hiw-h2">Architecture</h2>
                    <p className="hiw-lead">
                        A serverless, event-driven system on AWS. No servers to run, and an idle
                        account costs effectively nothing.
                    </p>
                    <ArchitectureDiagram />
                </section>

                <section className="hiw-section">
                    <h2 className="hiw-h2">One message, end to end</h2>
                    <p className="hiw-lead">
                        What happens between pressing send and seeing a reply.
                    </p>
                    <SequenceDiagram />
                </section>

                <section className="hiw-section">
                    <h2 className="hiw-h2">Technical detail</h2>
                    <div className="hiw-stack">
                        {STACK.map(s => (
                            <section className="hiw-stack-card" key={s.label}>
                                <h3 className="hiw-stack-label">{s.label}</h3>
                                <dl>
                                    {s.items.map(([term, desc]) => (
                                        <div className="hiw-stack-item" key={term}>
                                            <dt>{term}</dt>
                                            <dd>{desc}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </section>
                        ))}
                    </div>
                </section>

                <section className="hiw-cta-section">
                    <h2 className="hiw-cta-headline">
                        {isAuthed ? 'Back to your coach.' : 'Ready to start?'}
                    </h2>
                    <div className="hiw-ctas">
                        {isAuthed ? (
                            <button className="hiw-btn primary" onClick={() => navigate('/')}>
                                Open chat
                            </button>
                        ) : (
                            <>
                                <button className="hiw-btn primary" onClick={() => navigate('/auth')}>
                                    Get started
                                </button>
                                <button className="hiw-btn secondary" onClick={() => navigate('/auth?mode=signin')}>
                                    Sign in
                                </button>
                            </>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
